/* GENERATED from src/js/ndog-stock-guard.js — edit the source, then run `make js`. Never edit this file by hand. */
/*
  ndog-stock-guard — Gebinde-Bestand UI, shared by the redesign PDP (main-product_rd.liquid)
  and the legacy product section (main-product.liquid).

  Source of truth: data-stock="out" / data-max-qty on the options of the hidden
  select.original-selector, computed in Liquid (snippets/ndog-variant-stock.liquid —
  base unit via custom.basis_dose, else the variant's own stock).

  - marks variant tiles sold out (they stay clickable — the theme bundle owns their clicks)
  - disables #add (also the mobile sticky one — same element) with the "sold out" label
  - swaps the availability line ([data-ndog-availability])
  - caps the quantity at the packs the stock covers, greys out "+"
  - greys out the Einmalkauf/Spar-Abo options of ndog-variant-picker, if present

  Placed as an empty element inside the product form. Event-driven: listens to clicks,
  changes and input on the form — no permanent observers.
*/
if (!customElements.get('ndog-stock-guard')) {
  customElements.define(
    'ndog-stock-guard',
    class NdogStockGuard extends HTMLElement {
      // Collects the form elements and renders the initial state
      connectedCallback() {
        this.form = this.closest('form');
        this.origSelect = this.form ? this.form.querySelector('select.original-selector') : null;
        if (!this.origSelect) return;

        this.firstLevel = this.form.querySelector('.selector-wrapper.first-level');
        this.secondLevel = this.form.querySelector('.selector-wrapper.second-level');
        this.addButton = this.form.querySelector('#add');
        this.qtyInput = this.form.querySelector('input[name="quantity"]');
        this.qtyPlus = this.form.querySelector('.oci-plus');
        this.availabilityEl = document.querySelector('[data-ndog-availability]');
        this.soldOutLabel = this.dataset.labelSoldOut || 'Ausverkauft';

        this.onInteraction = this.onInteraction.bind(this);
        ['click', 'change', 'input', 'ndog:purchase-ready'].forEach((type) => {
          this.form.addEventListener(type, this.onInteraction);
        });

        this.markSoldOutSelectOptions();
        this.onInteraction();
      }

      // Removes listeners and timers
      disconnectedCallback() {
        if (this.form) {
          ['click', 'change', 'input', 'ndog:purchase-ready'].forEach((type) => {
            this.form.removeEventListener(type, this.onInteraction);
          });
        }
        clearTimeout(this.tickTimer);
        clearTimeout(this.lateTimer);
      }

      // Renders after the theme has handled the click/change, and once more after its delayed syncs
      onInteraction() {
        clearTimeout(this.tickTimer);
        clearTimeout(this.lateTimer);
        // NOTE: the theme (all.js.min.js) updates tiles/select/quantity in its own handlers,
        // partly with short timeouts — next tick + 400ms catch both without an observer.
        this.tickTimer = setTimeout(() => this.render(), 0);
        this.lateTimer = setTimeout(() => this.render(), 400);
      }

      /* ---- lookup ---- */

      // Returns the data-value of the active tile in a selector level ('' if none)
      getActiveValue(level) {
        const tile = level ? level.querySelector('.one-variation-selection.active') : null;
        return tile ? tile.getAttribute('data-value') : '';
      }

      // Finds the original-selector option for an option1 (+ optional option2) combination
      findVariantOption(option1, option2) {
        return Array.from(this.origSelect.options).find((option) =>
          option.getAttribute('data-option1') === option1 &&
          (option2 == null || option.getAttribute('data-option2') === option2)
        ) || null;
      }

      // True when the original-selector option is marked sold out by Liquid
      isOptionSoldOut(option) {
        return !!option && option.getAttribute('data-stock') === 'out';
      }

      // The selected variant, read from the tiles (independent of when the theme syncs the select)
      getSelectedOption() {
        const option1 = this.getActiveValue(this.firstLevel);
        let selected = null;
        if (option1) {
          selected = this.secondLevel
            ? this.findVariantOption(option1, this.getActiveValue(this.secondLevel))
            : this.findVariantOption(option1, null);
        }
        return selected || this.origSelect.options[this.origSelect.selectedIndex] || null;
      }

      /* ---- render ---- */

      // Applies the sold-out state of all tiles and of the selected variant
      render() {
        this.markTiles();
        const selected = this.getSelectedOption();
        const soldOut = this.isOptionSoldOut(selected);
        const maxQty = selected ? parseInt(selected.getAttribute('data-max-qty'), 10) : NaN;
        this.maxQty = maxQty > 0 && !soldOut ? maxQty : null;

        this.renderAddButton(soldOut);
        this.renderAvailability(soldOut);
        this.renderPurchaseOptions(soldOut);
        this.clampQuantity();
      }

      // Marks every visible variant tile for the current first-level choice
      markTiles() {
        const option1 = this.getActiveValue(this.firstLevel);
        const level = this.secondLevel || this.firstLevel;
        if (!level) return;
        level.querySelectorAll('.one-variation-selection').forEach((tile) => {
          const value = tile.getAttribute('data-value');
          const option = this.secondLevel ? this.findVariantOption(option1, value) : this.findVariantOption(value, null);
          this.markTile(tile, this.isOptionSoldOut(option));
        });
      }

      // Toggles the sold-out look + visible tag on one tile
      markTile(tile, soldOut) {
        tile.classList.toggle('is-sold-out', soldOut);
        let tag = tile.querySelector('.ndog-vp__sold-out');
        if (soldOut && !tag) {
          tag = document.createElement('span');
          tag.className = 'ndog-vp__sold-out';
          tag.textContent = this.soldOutLabel;
          // Directly under a visible price; bundle tiles hide theirs -> append to the tile
          const price = tile.querySelector('.selection-price');
          if (price && price.offsetParent !== null) {
            price.after(tag);
          } else {
            tile.appendChild(tag);
          }
        }
        if (tag) tag.hidden = !soldOut;
      }

      // Disables the add-to-cart button while the selected variant is sold out
      renderAddButton(soldOut) {
        const button = this.addButton;
        if (!button) return;
        if (this.addLabel == null) this.addLabel = button.textContent.trim();
        if (button.classList.contains('is-sold-out') === soldOut && button.disabled === soldOut) return;
        button.disabled = soldOut;
        button.classList.toggle('is-sold-out', soldOut);
        if (soldOut) {
          button.setAttribute('aria-disabled', 'true');
        } else {
          button.removeAttribute('aria-disabled');
        }
        button.textContent = soldOut ? this.soldOutLabel : this.addLabel;
      }

      // Swaps the availability line between the section's "available" / "not available" texts
      renderAvailability(soldOut) {
        const el = this.availabilityEl;
        if (!el) return;
        const html = soldOut ? el.getAttribute('data-text-unavailable') : el.getAttribute('data-text-available');
        el.classList.toggle('not', soldOut);
        if (html != null && el.innerHTML !== html) el.innerHTML = html;
      }

      // Greys out ndog-variant-picker's Einmalkauf/Spar-Abo options (built async after Seal)
      renderPurchaseOptions(soldOut) {
        const purchase = this.form.querySelector('.ndog-vp__purchase');
        if (!purchase) return;
        purchase.classList.toggle('is-sold-out', soldOut);
        purchase.querySelectorAll('input[type="radio"]').forEach((radio) => { radio.disabled = soldOut; });
      }

      // Caps the quantity at data-max-qty and greys out "+" at the cap
      clampQuantity() {
        if (!this.qtyInput) return;
        const value = parseInt(this.qtyInput.value, 10) || 1;
        if (this.maxQty && value > this.maxQty) {
          this.qtyInput.value = String(this.maxQty);
          // Lets price scripts (theme + ndog-variant-picker) re-render for the new quantity
          this.qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        if (!this.qtyPlus) return;
        const atMax = !!this.maxQty && (parseInt(this.qtyInput.value, 10) || 1) >= this.maxQty;
        this.qtyPlus.classList.toggle('is-disabled', atMax);
        if (atMax) {
          this.qtyPlus.setAttribute('aria-disabled', 'true');
        } else {
          this.qtyPlus.removeAttribute('aria-disabled');
        }
      }

      // Appends "– Ausverkauft" to ndog-variant-picker's option1 select entries whose variants are ALL sold out
      markSoldOutSelectOptions() {
        const select = this.form.querySelector('[data-ndog-variant-select]');
        if (!select) return;
        const variants = Array.from(this.origSelect.options);
        Array.from(select.options).forEach((entry) => {
          const group = variants.filter((option) => option.getAttribute('data-option1') === entry.value);
          if (group.length && group.every((option) => this.isOptionSoldOut(option))) {
            entry.textContent = entry.value + ' – ' + this.soldOutLabel;
          }
        });
      }
    }
  );
}
